// Session réelle (Supabase Auth) — identité du compte connecté.
import type { PermissionMap } from "@/store/permissions/catalog";
// Aucune donnée de gestion ici : uniquement l'identité du compte connecté.

export type AccountRole =
  | "dg_proprietaire"
  | "dg_agence"
  | "employe_agence"
  | "super_admin";

export type AccountStatus = "actif" | "en_attente" | "suspendu" | "desactive" | "refuse";

export interface SessionUser {
  id: string;
  email: string;
  prenom: string;
  nom: string;
  role: AccountRole;
  statut: AccountStatus;
  /** Fonction affichée dans l'espace (ex. « Directrice d'agence »). */
  fonction: string;
  /** Agence de rattachement (comptes Agence et Employés). */
  agence?: string;
  /** Permissions effectives (employé) : { module: { action: boolean } }. */
  permissions?: PermissionMap;
  /** Espace de rattachement effectif (employé : agence ou propriétaire). */
  scope?: "agence" | "proprietaire";
  /**
   * Raison réelle du blocage renvoyée par le serveur (ex. offre de lancement
   * déjà utilisée, demande de paiement en attente…). Affichée à la place du
   * message générique pour ne jamais laisser un compte bloqué sans explication.
   */
  raisonBlocage?: string;
  /** Code machine de la raison de blocage (ex. `essai_deja_consomme`). */
  raisonCode?: string;
}

export const ROLE_LABELS: Record<AccountRole, string> = {
  dg_proprietaire: "Propriétaire (DG)",
  dg_agence: "Directeur d'agence",
  employe_agence: "Employé d'agence",
  super_admin: "Super administrateur",
};

export const STATUS_LABELS: Record<AccountStatus, string> = {
  actif: "Actif",
  en_attente: "En attente",
  suspendu: "Suspendu",
  desactive: "Désactivé",
  refuse: "Refusé",
};

/**
 * Seul un compte « actif » peut accéder à son espace.
 * Les comptes en attente, suspendus ou désactivés sont bloqués.
 */
export function isBlockedStatus(statut: AccountStatus): boolean {
  return statut !== "actif";
}

/** Espace d'accueil selon le rôle. */
export function homePathForRole(role: AccountRole): string {
  switch (role) {
    case "dg_proprietaire":
      return "/proprietaire";
    case "dg_agence":
    case "employe_agence":
      return "/agence";
    case "super_admin":
      return "/super-admin";
    default:
      return "/connexion";
  }
}

/** Espace d'accueil d'un utilisateur, en tenant compte du scope d'un employé. */
export function homePathForUser(user: Pick<SessionUser, "role" | "scope">): string {
  if (user.role === "employe_agence") {
    return user.scope === "proprietaire" ? "/proprietaire" : "/agence";
  }
  return homePathForRole(user.role);
}

/** Libellé de l'espace selon le rôle. */
export function spaceLabelForRole(role: AccountRole): string {
  switch (role) {
    case "dg_proprietaire":
      return "Espace Propriétaire";
    case "dg_agence":
    case "employe_agence":
      return "Espace Agence";
    case "super_admin":
      return "Espace Super Admin";
    default:
      return "";
  }
}

export function fullName(user: Pick<SessionUser, "prenom" | "nom">): string {
  return [user.prenom, user.nom].filter(Boolean).join(" ").trim() || "Utilisateur";
}

/** Rôles autorisés par espace protégé. */
export const SPACE_ROLES: Record<string, AccountRole[]> = {
  proprietaire: ["dg_proprietaire"],
  agence: ["dg_agence", "employe_agence"],
  "super-admin": ["super_admin"],
};